import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
} from '../hooks/useService';
import urls from '../util/urls';
import { ServiceAuth } from '../hooks/useService/useService';
import { pick } from 'lodash';

interface Options {
  axiosOptions?: AxiosRequestConfig;
  [key: string]: any;
}

type BaseRange = {
  lower?: number;
  upper?: number;
  label: string;
};

export type Ranges = {
  'in-range'?: BaseRange & { status: 'in-range' };
  attention?: BaseRange & { status: 'attention' };
  optimal?: BaseRange & { status: 'optimal' };
};

type ResultWithRange = {
  type: 'withRange';
  resource: fhir.Observation;
  status: 'in-range' | 'attention' | 'optimal';
  ranges?: Ranges;
};

type ResultWithoutRange = {
  type: 'withoutRange';
  resource: fhir.Observation;
  status: 'default';
};

type PendingResult = {
  type: 'pending';
  resource: fhir.Observation;
  status: 'pending';
};

type IndividualResult = ResultWithRange | ResultWithoutRange | PendingResult;

type Coding = {
  code: string;
  system: string;
  display: string;
};
type System = {
  code: string;
  system: string;
  display?: string;
};

type CodingAndSystems = { coding: Coding } & { systems: System[] } & {
  aboutText: string;
};

export type Result = CodingAndSystems & {
  results: IndividualResult[];
};

type ResultsResponse = {
  items: Result[];
  links: {
    self: string;
    next?: string;
  };
};

type ResultStatusAndText = {
  result: {
    status: 'in-range' | 'attention' | 'optimal' | 'default';
    text: string;
    interpretationText?: string;
  };
};

export type ResultHistoryWithRange = Omit<ResultWithRange, 'status'> &
  ResultStatusAndText &
  CodingAndSystems;

export type ResultHistoryWithoutRange = Omit<
  ResultWithoutRange | PendingResult,
  'status'
> &
  ResultStatusAndText &
  CodingAndSystems;

export type ResultHistory = ResultHistoryWithRange | ResultHistoryWithoutRange;

type ResultHistoryResponse = {
  items: ResultHistory[];
  links: {
    self: string;
    next?: string;
  };
};

const isWithRange = (result: IndividualResult | ResultHistory): boolean => {
  return result.type === 'withRange';
};

export const isResultWithRange = (
  result: IndividualResult,
): result is ResultWithRange => isWithRange(result);

export const isResultHistoryWithRange = (
  result: ResultHistory,
): result is ResultHistoryWithRange => isWithRange(result);

export type MedicalResultSource =
  | 'mappedLabs'
  | 'unmappedLabs'
  | 'memberUpload';

interface BaseParams {
  patientId: string;
  project: string;
  nextPageToken?: string | null;
  pageSize?: number | null;
  cohort?: string;
  resultCount?: string;
  code?: string;
  system?: string;
  includeClinicOnly?: boolean;
  source?: MedicalResultSource;
}

export default class MedicalResultsService {
  medicalResultsAPI: (auth: ServiceAuth) => AxiosInstance;
  constructor(_options?: Partial<Options>) {
    this.medicalResultsAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  getResults =
    <TResponse = ResultsResponse>(auth: ServiceAuth) =>
    async (params: BaseParams): Promise<AxiosResponse<TResponse>> => {
      return this.medicalResultsAPI(auth).get<TResponse>(
        urls.api.medicalResults.results(
          params.patientId,
          pick(params, ['project', 'nextPageToken', 'pageSize', 'cohort']),
        ),
      );
    };

  getResultsV3 =
    <TResponse = ResultsResponse>(auth: ServiceAuth) =>
    async (params: BaseParams): Promise<AxiosResponse<TResponse>> => {
      return this.medicalResultsAPI(auth).get<TResponse>(
        urls.api.medicalResults.resultsV3(
          params.patientId,
          pick(params, [
            'project',
            'nextPageToken',
            'pageSize',
            'cohort',
            'resultCount',
            'includeClinicOnly',
            'source',
          ]),
        ),
      );
    };

  getHistory =
    <TResponse = ResultHistoryResponse>(auth: ServiceAuth) =>
    async (params: BaseParams): Promise<AxiosResponse<TResponse>> => {
      return this.medicalResultsAPI(auth).get<TResponse>(
        urls.api.medicalResults.history(
          params.patientId,
          pick(params, [
            'project',
            'cohort',
            'code',
            'system',
            'includeClinicOnly',
            'source',
          ]),
        ),
      );
    };
}
